import React from 'react';
import { motion } from 'framer-motion';

export interface PremiumBackgroundProps {
  variant: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  className?: string;
}

export const PremiumBackground: React.FC<PremiumBackgroundProps> = ({ variant, className = '' }) => {
  const baseClasses = `absolute inset-0 overflow-hidden pointer-events-none z-0 ${className}`;

  
  switch (variant) {
    case 1: // Premium Silk (Floors 4 & 12)
      return (
        <div className={baseClasses}>
          <div className="absolute inset-0 opacity-[0.6]" style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.9) 0%, transparent 80%)'
          }} />
          <motion.div 
            className="absolute inset-0 opacity-[0.6]"
            style={{
              background: 'linear-gradient(135deg, rgba(30,58,138,0.04) 0%, rgba(255,255,255,0.9) 30%, rgba(15,23,42,0.03) 50%, rgba(255,255,255,1) 70%, rgba(212,175,55,0.03) 100%)',
              backgroundSize: '200% 200%'
            }}
            animate={{ backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute inset-0 backdrop-blur-[6px]" />
        </div>
      );
      
    case 2: // Frosted Metal
      return (
        <div className={baseClasses}>
          <div className="absolute inset-0 opacity-[0.12]" style={{
            backgroundImage: `repeating-linear-gradient(45deg, #8C6D37 0, #8C6D37 1px, transparent 1px, transparent 6px)`
          }} />
          <div className="absolute inset-0 backdrop-blur-[4px] bg-white/10 dark:bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(212,175,55,0.06)] to-transparent" />
        </div>
      );

    case 3: // Abstract Topography
      return (
        <div className={baseClasses}>
          <div className="absolute inset-0 opacity-[0.25]" style={{
            background: `repeating-radial-gradient(circle at 100% 0%, transparent 0, transparent 40px, rgba(212,175,55,0.4) 41px, transparent 42px)`,
            backgroundSize: '100% 100%'
          }} />
          <div className="absolute inset-0 opacity-[0.2]" style={{
            background: `repeating-radial-gradient(circle at 0% 100%, transparent 0, transparent 60px, rgba(212,175,55,0.3) 61px, transparent 62px)`
          }} />
        </div>
      );

    case 4: // Heavyweight Cotton
      return (
        <div className={baseClasses}>
          <div className="absolute inset-0 opacity-[0.15] mix-blend-multiply dark:mix-blend-overlay" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            backgroundSize: '150px 150px'
          }} />
          <div className="absolute inset-0 shadow-[inset_0_0_150px_rgba(0,0,0,0.05)]" />
        </div>
      );

    case 5: // Gold Dust Particles
      const particles = Array.from({ length: 30 });
      return (
        <div className={baseClasses}>
          {particles.map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-[#D4AF37]"
              style={{
                width: (i % 4) + 2 + 'px',
                height: (i % 4) + 2 + 'px',
                left: (i * 13) % 100 + '%',
                top: (i * 17) % 100 + '%',
                opacity: 0.6,
                boxShadow: '0 0 12px 2px rgba(212,175,55,0.6)'
              }}
              animate={{
                y: [0, -50, 0],
                x: [0, (i % 2 === 0 ? 30 : -30), 0],
                opacity: [0.2, 0.8, 0.2]
              }}
              transition={{
                duration: 8 + (i % 8),
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          ))}
        </div>
      );

    case 6: // Liquid Prism
      return (
        <div className={baseClasses}>
          <motion.div 
            className="absolute -top-[10%] -left-[10%] w-[60%] h-[60%] rounded-full bg-[#D4AF37]/20 blur-[80px]"
            animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute top-[30%] -right-[10%] w-[50%] h-[70%] rounded-full bg-[#3B82F6]/15 blur-[100px]"
            animate={{ x: [0, -50, 0], y: [0, -60, 0] }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      );

    case 7: // Micro-Grid
      return (
        <div className={baseClasses}>
          <div className="absolute inset-0 opacity-[0.25]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M40 0L0 0L0 40' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3C/svg%3E")`,
            backgroundSize: '40px 40px'
          }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(255,255,255,0.3)] dark:via-[rgba(0,0,0,0.5)] to-transparent" />
        </div>
      );

    case 8: // Clean Chiaroscuro (Floors 2 & 13) - Removed Noise/Ripples!
      return (
        <div className={baseClasses}>
          <div className="absolute inset-0" style={{
            background: `radial-gradient(circle at 20% 0%, rgba(212,175,55,0.15) 0%, transparent 60%), radial-gradient(circle at 100% 100%, rgba(255,255,255,0.1) 0%, transparent 70%)`
          }} />
          <motion.div 
            className="absolute inset-0 opacity-40 mix-blend-overlay"
            style={{
              background: 'linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.6) 45%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.6) 55%, transparent 60%)',
              backgroundSize: '300% 300%'
            }}
            animate={{ backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      );

    case 9: // Fluid Gold
      return (
        <div className={baseClasses}>
          <motion.div 
            className="absolute inset-0 opacity-[0.2]"
            style={{
              background: 'linear-gradient(120deg, transparent, rgba(212,175,55,0.8), transparent)',
              backgroundSize: '200% 100%'
            }}
            animate={{ backgroundPosition: ['200% 0', '-200% 0'] }}
            transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          />
          <div className="absolute inset-0 backdrop-blur-[20px]" />
        </div>
      );

    case 10: // Signature Blend (Floors 1 & 16) - Reduced dots and brightness
      return (
        <div className={baseClasses}>
          <div className="absolute inset-0 opacity-[0.08]" style={{
             backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23D4AF37'/%3E%3C/svg%3E")`
          }} />
          <div className="absolute inset-0" style={{
            background: `radial-gradient(ellipse at 50% -20%, rgba(212,175,55,0.12) 0%, transparent 60%)`
          }} />
          <motion.div 
            className="absolute top-[20%] right-[10%] w-[40%] h-[50%] rounded-full bg-[#D4AF37]/8 blur-[80px]"
            animate={{ y: [-20, 20, -20], opacity: [0.3, 0.7, 0.3] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      );

    default:
      return null;
  }

};
